import { User, AuthResponse, UserRole } from '../types/user';
import { configureSupabaseClient, supabase, clearSupabaseAuthStorage, checkSessionError } from '../utils/supabaseClient';
// import { apiClient } from './apiClient';
class AuthService {
	/**
	 * Login with email and password
	 */
	async login(email: string, password: string, rememberMe: boolean): Promise<AuthResponse> {
		try {
			const client = configureSupabaseClient(rememberMe);

			const { data, error } = await client.auth.signInWithPassword({
				email,
				password
			});

			if (error) {
				throw new Error(error.message);
			}

			if (!data.user || !data.session) {
				throw new Error('No user data or session returned');
			}

			const response: AuthResponse = {
				user: {
					id: data.user.id,
					name: data.user.user_metadata?.name || 'User',
					email: data.user.email!,
					role: data.user.role as UserRole,
					createdAt: data.user.created_at,
					updatedAt: new Date().toISOString()
				},
				token: data.session.access_token
			};

			return response;
		} catch (error) {
			if (error instanceof Error) {
				throw new Error(error.message || 'Login failed');
			}
			throw new Error('Unknown error occurred during login');
		}
	}

	/**
	 * Register a new user
	 */
	async register(email: string, password: string, name: string): Promise<AuthResponse> {
		// Simulate registration API call
		return new Promise((resolve) => {
			setTimeout(() => {
				const response: AuthResponse = {
					user: {
						id: Math.random().toString(36).substr(2, 9),
						name,
						email,
						role: 'admin',
						createdAt: new Date().toISOString(),
						updatedAt: new Date().toISOString()
					},
					// restaurant: {
					// 	id: '',
					// 	name: '',
					// 	created_at: '',
					// 	is_setup: false
					// },
					token: 'dummy-jwt-token'
				};
				resolve(response);
			}, 800);
		});

		// In a real implementation:
		// return apiClient.post<AuthResponse>('/auth/register', { email, password, name });
	}

	/**
	 * Logout the current user
	 */
	async logout(): Promise<void> {
		try {
			// First sign out from Supabase
			await supabase.auth.signOut();

			// Clear any remaining auth data
			if (typeof window !== 'undefined') {
				clearSupabaseAuthStorage(window.localStorage);
				clearSupabaseAuthStorage(window.sessionStorage);
			}

			// Don't redirect here - let the component handle navigation
			// This prevents the brief "not found" page flash
		} catch (error) {
			console.error('Logout error:', error);
			// Even if logout fails, clear local data
			if (typeof window !== 'undefined') {
				clearSupabaseAuthStorage(window.localStorage);
				clearSupabaseAuthStorage(window.sessionStorage);
			}
			throw error; // Re-throw so component can handle the error
		}
	}

	/**
	 * Get the current logged-in user
	 */
	// async getCurrentUser(token: string): Promise<User> {
	// 	// Simulate API call to get current user
	// 	return new Promise((resolve, reject) => {
	// 		setTimeout(() => {
	// 			// const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
	// 			if (token) {
	// 				const user: User = {
	// 					id: '1',
	// 					name: 'Demo User',
	// 					email: 'demo@example.com',
	// 					role: 'admin',
	// 					createdAt: new Date().toISOString(),
	// 					updatedAt: new Date().toISOString()
	// 				};
	// 				resolve(user);
	// 			} else {
	// 				reject(new Error('No token found'));
	// 			}
	// 		}, 500);
	// 	});

	// 	// In a real implementation:
	// 	// return apiClient.get<User>('/auth/me');
	// }

	async getCurrentUser(): Promise<User> {
		try {
			// First, try to get the existing session from storage
			const { data: { session }, error: sessionError } = await supabase.auth.getSession();

			// Check for session expired error
			if (sessionError) {
				checkSessionError(sessionError);
				throw sessionError;
			}

			// If we have a session, check if it needs refresh
			if (session) {
				// Check if session is about to expire (within 5 minutes)
				const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
				const now = Date.now();
				const fiveMinutes = 5 * 60 * 1000;

				if (expiresAt - now < fiveMinutes) {
					// Session is about to expire or already expired, try to refresh
					const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();

					if (refreshError) {
						checkSessionError(refreshError);
						throw refreshError;
					}

					if (refreshedSession) {
						return {
							id: refreshedSession.user.id,
							name: refreshedSession.user.user_metadata?.name || 'User',
							email: refreshedSession.user.email ?? '',
							role: refreshedSession.user.role as UserRole,
							createdAt: refreshedSession.user.created_at,
							updatedAt: new Date().toISOString()
						} as User;
					}
				}

				// Session is still valid, return user data
				return {
					id: session.user.id,
					name: session.user.user_metadata?.name || 'User',
					email: session.user.email ?? '',
					role: session.user.role as UserRole,
					createdAt: session.user.created_at,
					updatedAt: new Date().toISOString()
				} as User;
			}

			// No session found
			throw new Error('No active session');
		} catch (error) {
			if (error instanceof Error) {
				checkSessionError(error);
			}
			throw error;
		}
	}
	/**
	 * Request password reset
	 */
	async forgotPassword(email: string): Promise<void> {
		// Simulate API call
		return new Promise((resolve) => {
			setTimeout(() => {
				resolve();
			}, 800);
		});

		// In a real implementation:
		// return apiClient.post('/auth/forgot-password', { email });
	}

	/**
	 * Reset password with token
	 */
	async resetPassword(token: string, password: string): Promise<void> {
		// Simulate API call
		return new Promise((resolve) => {
			setTimeout(() => {
				resolve();
			}, 800);
		});

		// In a real implementation:
		// return apiClient.post('/auth/reset-password', { token, password });
	}
}

export const authService = new AuthService();